import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Bell, BellOff, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { subscribeToPush, unsubscribeFromPush } from '@/lib/push';

const COMMON_SYMBOLS = ['BTC','ETH','BNB','SOL','XRP','ADA','DOGE','AVAX','DOT','MATIC','LINK','LTC','TRX','SHIB','UNI'];

function PushToggle() {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setEnabled(!!sub && Notification.permission === 'granted');
    }).catch(() => {});
  }, [supported]);

  if (!supported) {
    return <div className="text-xs text-[#5E6673]">Push notifications not supported in this browser</div>;
  }

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setEnabled(true);
      toast.success('Push notifications enabled');
    } catch (e) { toast.error(e.message || 'Failed to enable push'); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setEnabled(false);
      toast.success('Push notifications disabled');
    } catch (e) { toast.error('Failed to disable'); }
    finally { setBusy(false); }
  };

  return (
    <Button size="sm" variant="outline" onClick={enabled ? disable : enable} disabled={busy}
      className={`border-[#2B3139] bg-[#181A20] rounded-sm ${enabled ? 'text-[#0ECB81] hover:text-[#0ECB81]' : 'text-white'} hover:bg-[#2B3139]`}
      data-testid="push-toggle-btn">
      {enabled ? <><Bell className="w-3.5 h-3.5 mr-1.5" /> Push On</> : <><BellOff className="w-3.5 h-3.5 mr-1.5" /> Enable Push</>}
    </Button>
  );
}

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [symbol, setSymbol] = useState('BTC');
  const [direction, setDirection] = useState('above');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get('/alerts');
    setAlerts(r.data.alerts);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post('/alerts', { symbol, direction, target_price: parseFloat(target) });
      toast.success(`Alert created for ${symbol}`);
      setOpen(false); setTarget('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create alert'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/alerts/${id}`); toast.success('Alert removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const active = alerts.filter(a => a.status === 'active');
  const triggered = alerts.filter(a => a.status === 'triggered');

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Price Alerts</h1>
          <p className="text-[#848E9C] text-sm mt-1">Get notified by email + browser push the moment your target price hits.</p>
        </div>
        <div className="flex gap-2">
          <PushToggle />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm" data-testid="new-alert-btn">
              <Bell className="w-4 h-4 mr-1.5" /> New Alert
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm">
            <DialogHeader><DialogTitle>Create Price Alert</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Symbol</Label>
                <Select value={symbol} onValueChange={setSymbol}>
                  <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1.5" data-testid="alert-symbol"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181A20] border-[#2B3139] text-white max-h-72">
                    {COMMON_SYMBOLS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Trigger When Price</Label>
                <Select value={direction} onValueChange={setDirection}>
                  <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1.5" data-testid="alert-direction"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                    <SelectItem value="above">Crosses above target ▲</SelectItem>
                    <SelectItem value="below">Drops below target ▼</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Target Price (USD)</Label>
                <Input type="number" value={target} onChange={e=>setTarget(e.target.value)} placeholder="e.g. 70000"
                  className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono mt-1.5 focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                  data-testid="alert-target" />
              </div>
              <Button onClick={create} disabled={busy || !target} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="confirm-alert-btn">
                {busy ? 'Creating…' : 'Create Alert'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <h2 className="text-white text-xl font-semibold mb-3">Active ({active.length})</h2>
      <div className="border border-[#2B3139] bg-[#181A20] mb-8" data-testid="active-alerts">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-2">Symbol</div>
          <div className="col-span-3">Condition</div>
          <div className="col-span-3 text-right">Target</div>
          <div className="col-span-3">Created</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {active.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No active alerts. Create one to get notified.</div>
        ) : active.map(a => (
          <div key={a.alert_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center">
            <div className="col-span-2 text-white font-medium">{a.symbol}</div>
            <div className="col-span-3 flex items-center gap-1 text-sm">
              {a.direction === 'above' ? <ArrowUp className="w-3.5 h-3.5 text-[#0ECB81]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#F6465D]" />}
              <span className={a.direction === 'above' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>{a.direction}</span>
            </div>
            <div className="col-span-3 text-right font-mono text-white">${a.target_price.toLocaleString()}</div>
            <div className="col-span-3 text-[#848E9C] text-xs">{new Date(a.created_at).toLocaleString()}</div>
            <div className="col-span-1 text-right">
              <button onClick={() => remove(a.alert_id)} className="text-[#F6465D] hover:opacity-80" data-testid={`del-alert-${a.alert_id}`}>
                <Trash2 className="w-4 h-4 inline" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-white text-xl font-semibold mb-3">Triggered ({triggered.length})</h2>
      <div className="border border-[#2B3139] bg-[#181A20]">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-2">Symbol</div>
          <div className="col-span-3">Condition</div>
          <div className="col-span-3 text-right">Target / Hit</div>
          <div className="col-span-4">Triggered</div>
        </div>
        {triggered.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No triggered alerts yet.</div>
        ) : triggered.map(a => (
          <div key={a.alert_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center">
            <div className="col-span-2 text-white font-medium">{a.symbol}</div>
            <div className="col-span-3 text-sm capitalize text-[#B7BDC6]">{a.direction}</div>
            <div className="col-span-3 text-right font-mono text-sm">
              <div className="text-[#848E9C]">${a.target_price.toLocaleString()}</div>
              <div className="text-[#FCD535]">${a.triggered_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
            <div className="col-span-4 text-[#848E9C] text-xs">{a.triggered_at && new Date(a.triggered_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
